import { useEffect } from 'react';
import { Spin, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientBookingsStore } from '../../store/clientBookingsStore';
import { useBookingFlowStore } from '../../store/bookingFlowStore';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import type { ClientBookingItem } from '../../types/client';
import { BookingCard } from '../../components/client/bookings/BookingCard';
import { SegmentControl } from '../../components/client/bookings/SegmentControl';
import { SmartHintBlock } from '../../components/client/bookings/SmartHintBlock';
import BookingCardSkeletonList from '../../components/client/bookings/BookingCardSkeleton';
import { EmptyBookings } from '../../components/client/bookings/EmptyBookings';
import { ErrorBookings } from '../../components/client/bookings/ErrorBookings';
import { HistoryBookingList } from '../../components/client/bookings/HistoryBookingList';
import PullToRefreshIndicator from '../../components/shared/PullToRefreshIndicator';

function groupByDate(bookings: ClientBookingItem[]): { date: string; items: ClientBookingItem[] }[] {
  const map = new Map<string, ClientBookingItem[]>();
  for (const b of bookings) {
    const group = map.get(b.date) ?? [];
    group.push(b);
    map.set(b.date, group);
  }
  return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
}

export default function BookingsTab() {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const store = useClientBookingsStore();

  const formatDateHeader = (dateStr: string): string => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const d = new Date(dateStr + 'T00:00:00');
    const monthLabel = t(`bookings.dateHeader.months.${d.getMonth()}`);
    const label = `${d.getDate()} ${monthLabel}`;
    if (dateStr === today.toISOString().slice(0, 10)) return `${t('bookings.dateHeader.today')} · ${label}`;
    if (dateStr === tomorrow.toISOString().slice(0, 10)) return `${t('bookings.dateHeader.tomorrow')} · ${label}`;
    return `${t(`bookings.dateHeader.weekdays.${d.getDay()}`)} · ${label}`;
  };
  const storeStatus = useClientBookingsStore((s) => s.status);
  const refresh = useClientBookingsStore((s) => s.refresh);
  const resetFlow = useBookingFlowStore((s) => s.resetFlow);
  const seedFromRebook = useBookingFlowStore((s) => s.seedFromRebook);

  // Mount: refresh once
  useEffect(() => {
    void refresh();
  }, []); // mount-only: fetch once on tab render

  // Window focus bo'lganda silent refresh
  useEffect(() => {
    const onFocus = () => {
      if (storeStatus === 'ready') {
        void refresh();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [storeStatus, refresh]);

  // Til o'zgarganda qayta yuklash clientBookingsStore modulida singleton
  // listener sifatida bajariladi — komponent unmount paytida ham ishlaydi.

  const handleRebook = (booking: ClientBookingItem) => {
    resetFlow();
    seedFromRebook(booking); // store-based seeding saqlanadi (backward compat)
    navigate(`/booking/new/0?serviceId=${booking.serviceId}&providerId=${booking.providerId}&source=rebook`);
  };

  const handleCancelRequest = (_id: number, uuid: string) => {
    navigate(`/bookings/${uuid}/cancel`);
  };

  const handleTabChange = (tab: 'active' | 'history') => {
    store.setTab(tab);
    // Tab switch bo'lganda refresh (agar hozir loading bo'lmasa)
    if (storeStatus !== 'loading' && storeStatus !== 'loading-more') {
      void refresh();
    }
  };

  const showLoadingMore = store.status === 'loading-more';

  const { containerRef: pullRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({
      onRefresh: refresh,
      disabled: storeStatus === 'loading',
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '8px 16px', flexShrink: 0 }}>
        <SegmentControl
          value={store.tab}
          activeCount={store.activeCount}
          historyCount={store.historyCount}
          onChange={handleTabChange}
          disabled={store.status === 'loading'}
        />
      </div>

      {store.hint !== null && (
        <SmartHintBlock hint={store.hint} />
      )}

      <div
        ref={pullRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          position: 'relative',
        }}
      >
        <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} progress={progress} />
        {store.status === 'loading' && (
          <div key="loading" className="bl-animate-in">
            <BookingCardSkeletonList />
          </div>
        )}

        {store.status === 'error' && store.error !== null && (
          <div key="error" className="bl-animate-in">
            <ErrorBookings
              message={store.error.message}
              onRetry={() => void store.refresh()}
            />
          </div>
        )}

        {store.status === 'empty' && (
          <div key="empty" className="bl-animate-in">
            <EmptyBookings tab={store.tab} />
          </div>
        )}

        {(store.status === 'ready' || store.status === 'loading-more') && (
          <div key="ready" className="bl-animate-in">
            {store.tab === 'active' ? (
              groupByDate(store.active).map(({ date, items }) => (
                <div key={date}>
                  <div style={{
                    position: 'sticky', top: 0, zIndex: 10,
                    padding: '6px 16px',
                    background: 'var(--bl-background, var(--tg-theme-bg-color, #fff))',
                    borderBottom: '1px solid var(--bl-divider)',
                  }}>
                    <Typography.Text style={{ fontSize: 13, fontWeight: 600, color: 'var(--bl-text-secondary)' }}>
                      {formatDateHeader(date)}
                    </Typography.Text>
                  </div>
                  {items.map((b) => (
                    <BookingCard
                      key={b.id}
                      booking={b}
                      onCancelRequest={handleCancelRequest}
                    />
                  ))}
                </div>
              ))
            ) : store.error !== null && store.history.length === 0 ? (
              <ErrorBookings
                message={store.error.message}
                onRetry={() => void store.refresh()}
              />
            ) : (
              <HistoryBookingList
                bookings={store.history}
                hasMore={store.historyHasMore}
                isLoadingMore={showLoadingMore}
                onLoadMore={() => void store.loadMoreHistory()}
                onRebook={handleRebook}
              />
            )}
          </div>
        )}

        {store.status === 'loading-more' && store.tab === 'active' && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin size="small" />
          </div>
        )}
      </div>

    </div>
  );
}
